import { colors, intl, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC } from 'react'
import type { MediaStatus } from 'room/common/model/media-status'
import {
	RemoteControls,
	RemoteControlsPending,
	RemoteHeader,
} from './remote-controls'

const Card = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: min(100%, 720px);
	margin: auto;
	padding: 28px 32px;
	border-radius: 16px;
	background: ${colors.white};
	box-shadow: 0 6px 24px ${colors.black15};
`

const Notice = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	font-size: 15px;
	line-height: 20px;
	color: ${colors.brightBlackish};
`

const Failure = styled.span`
	font-weight: 700;
	color: ${colors.darkIndigo};
`

/** What the controls are not working for, read out with the disabled Play. */
const NOTICE_ID = 'presenter-media-remote-notice'

type Props = {
	name: string
	/** Whether the presentation window's player has the recording. */
	status: MediaStatus
	playing: boolean
	positionMs: number
	durationMs: number
	onPlayPause: () => void
	onSeek: (positionMs: number) => void
	/** Asks the presentation window to load the recording again. */
	onRetry: () => void
	onClose: () => void
}

/**
 * The console's controls for a recording while presenting: there is nothing
 * to see of it, so a card is all it takes. What it shows is the truth about
 * the player on the projector - loading, failed (with a way to try again) or
 * ready - never controls for a recording that is not there. A film has a
 * monitor of its own (`VideoMonitor`).
 */
export const MediaRemoteControl: FC<Props> = ({
	status,
	onRetry,
	onClose,
	...controls
}) => (
	<Card
		data-testid="presenter.media_remote"
		data-kind="audio"
		data-status={status}
		aria-busy={status === 'loading'}
	>
		<RemoteHeader name={controls.name} onClose={onClose} />
		{status === 'ready' ? (
			<RemoteControls {...controls} />
		) : (
			<>
				<Notice
					id={NOTICE_ID}
					role="status"
					data-testid="presenter.media_remote.status"
				>
					{status === 'loading' ? (
						<intl.Translate>in_person.presenter.media.loading</intl.Translate>
					) : (
						<>
							<Failure>
								<intl.Translate>
									in_person.presenter.media.failed
								</intl.Translate>
							</Failure>
							<intl.Translate>
								in_person.presenter.media.failed_hint
							</intl.Translate>
						</>
					)}
				</Notice>
				<RemoteControlsPending
					reasonId={NOTICE_ID}
					failed={status === 'failed'}
				>
					{status === 'failed' ? (
						<ui.Button
							small
							type="button"
							color="indigoBlue"
							backgroundColor="white"
							borderColor="indigoBlue"
							onClick={onRetry}
							data-testid="presenter.media_remote.retry"
						>
							<intl.Translate>in_person.presenter.media.retry</intl.Translate>
						</ui.Button>
					) : null}
				</RemoteControlsPending>
			</>
		)}
	</Card>
)
